import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Check, Search } from "lucide-react";
import SiteShell from "../../components/layout/SiteShell";
import ContentPageLayout from "../../components/content/ContentPageLayout";
import SEO from "../../components/seo/SEO";
import ConversionCtas from "../../components/content/ConversionCtas";
import WellnessDisclaimer from "../../components/content/WellnessDisclaimer";
import DarkCard from "../../components/layout/DarkCard";
import { HOME_CTA_DARK_OUTLINE, HOME_CTA_DARK_PRIMARY } from "../../lib/homeCta";
import {
  TRACE_MINERALS,
  TRACE_MINERALS_COUNT,
  TRACE_MINERALS_SPELLING_NOTE,
} from "../../lib/traceMinerals";
import {
  SEA_MOSS_AMINO_ACIDS,
  SEA_MOSS_AMINO_ACIDS_INTRO,
  SEA_MOSS_AMINO_ACIDS_VARIABILITY_NOTE,
} from "../../lib/seaMossAminoAcids";

const MINERAL_PREVIEW = 24;

const HUB_CARDS = [
  {
    id: "trace-minerals",
    title: "92 Trace Minerals",
    desc: "The classic educational list people associate with sea moss — with honest framing, not lab theatre.",
  },
  {
    id: "amino-acids",
    title: "Amino Acids",
    desc: "Nineteen amino acids commonly cited in sea moss nutrition conversations, with soft educational blurbs.",
  },
  {
    to: "/learn/sea-moss-research",
    title: "Research snapshot",
    desc: "Citations, iodine notes, and what evidence discussions actually cover.",
  },
  {
    to: "/learn/sea-moss-benefits",
    title: "Benefits (education)",
    desc: "What people commonly research — without miracle product claims.",
  },
];

function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function SeaMossNutritionPage() {
  const path = "/learn/sea-moss-nutrition";
  const location = useLocation();
  const [query, setQuery] = useState("");
  const [showAllMinerals, setShowAllMinerals] = useState(false);

  useEffect(() => {
    const id = (location.hash || "").replace("#", "");
    if (!id) return;
    requestAnimationFrame(() => scrollToId(id));
  }, [location.hash]);

  const filteredMinerals = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return TRACE_MINERALS.map((name, i) => ({ name, n: i + 1 }));
    return TRACE_MINERALS.map((name, i) => ({ name, n: i + 1 })).filter((m) =>
      m.name.toLowerCase().includes(q)
    );
  }, [query]);

  const isFiltering = query.trim().length > 0;
  const visibleMinerals =
    isFiltering || showAllMinerals ? filteredMinerals : filteredMinerals.slice(0, MINERAL_PREVIEW);

  return (
    <SiteShell theme="dark">
      <SEO
        title="Sea Moss Nutrition Resources | Minerals & Amino Acids | Moss Matrix"
        description="Educational Moss Matrix resource hub: the commonly cited 92 trace minerals list and amino acids associated with sea moss — claim-safe framing, not medical advice or lab guarantees."
        path={path}
      />
      <ContentPageLayout
        eyebrow="Learn · Resources"
        title="Sea moss nutrition, without the parchment dump."
        subtitle="A clean resource hub for the mineral and amino acid stories people look up — framed as education, not a certificate for every jar."
      >
        <DarkCard className="mb-10 max-w-3xl border border-gold/25">
          <p className="text-sm text-sage leading-relaxed m-0">
            Sea moss is often discussed in wellness circles for a broad mineral story and a range of amino acids.
            Composition varies by species and conditions. Moss Matrix sells fridge-ready gel as a food — we do not
            claim every jar has been lab-proven to contain all {TRACE_MINERALS_COUNT} listed minerals, or that every
            name on classic marketing lists is beneficial or present in meaningful amounts.
          </p>
        </DarkCard>

        <nav aria-label="Nutrition resource topics" className="grid sm:grid-cols-2 gap-4 mb-14">
          {HUB_CARDS.map((card) => {
            const inner = (
              <>
                <h2 className="font-sans font-semibold text-lg text-ivory m-0 group-hover:text-goldhover transition">
                  {card.title}
                </h2>
                <p className="mt-3 text-sm text-sage leading-relaxed flex-1 m-0">{card.desc}</p>
                <span className="mt-5 text-xs tracking-widest uppercase text-sage/80">
                  {card.to ? "Open →" : "Jump ↓"}
                </span>
              </>
            );

            if (card.to) {
              return (
                <Link key={card.title} to={card.to} className="group block h-full">
                  <DarkCard className="h-full flex flex-col transition group-hover:border-gold/40">{inner}</DarkCard>
                </Link>
              );
            }

            return (
              <a
                key={card.id}
                href={`#${card.id}`}
                className="group block h-full"
                onClick={(e) => {
                  e.preventDefault();
                  window.history.replaceState(null, "", `#${card.id}`);
                  scrollToId(card.id);
                }}
              >
                <DarkCard className="h-full flex flex-col transition group-hover:border-gold/40">{inner}</DarkCard>
              </a>
            );
          })}
        </nav>

        {/* —— Trace minerals —— */}
        <section id="trace-minerals" className="scroll-mt-28 mb-16" aria-labelledby="trace-minerals-heading">
          <header className="max-w-3xl mb-8">
            <p className="text-xs tracking-[0.25em] uppercase text-sage/80 m-0">Resource</p>
            <h2 id="trace-minerals-heading" className="font-sans font-semibold text-3xl md:text-4xl text-ivory mt-3 m-0">
              92 Trace Minerals
            </h2>
            <p className="mt-4 text-sm md:text-base text-sage leading-relaxed m-0">
              A commonly cited list associated with sea moss education and brand storytelling. Green ticks mark each
              name for scannability — they are not assay results. Classic lists include radioactive and toxic elements
              as folklore catalogue entries; presence on a list is not a health claim.
            </p>
            <p className="mt-3 text-xs text-sage/75 leading-relaxed m-0">{TRACE_MINERALS_SPELLING_NOTE}</p>
          </header>

          <div className="mb-5 flex flex-col sm:flex-row sm:items-center gap-3">
            <label className="relative flex-1 max-w-md">
              <span className="sr-only">Filter minerals</span>
              <Search
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-sage/70"
                strokeWidth={1.75}
                aria-hidden
              />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter the list…"
                className="w-full rounded-full border border-white/14 bg-panel/80 pl-10 pr-4 py-2.5 text-sm text-ivory placeholder:text-sage/50 outline-none focus:border-gold/45"
              />
            </label>
            <p className="text-xs text-sage/80 m-0 sm:ml-auto">
              {isFiltering
                ? `${filteredMinerals.length} match${filteredMinerals.length === 1 ? "" : "es"}`
                : `${TRACE_MINERALS_COUNT} names · educational list`}
            </p>
          </div>

          <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2.5 list-none m-0 p-0">
            {visibleMinerals.map((m) => (
              <li
                key={m.name}
                className="flex items-center gap-2 min-w-0 text-sm text-ivory/90"
              >
                <Check
                  className="w-4 h-4 shrink-0 text-emerald-400"
                  strokeWidth={2.5}
                  aria-hidden
                />
                <span className="truncate">
                  <span className="text-sage/55 tabular-nums mr-1.5">{m.n}.</span>
                  {m.name}
                </span>
              </li>
            ))}
          </ul>

          {!isFiltering && filteredMinerals.length > MINERAL_PREVIEW ? (
            <div className="mt-8">
              <button
                type="button"
                onClick={() => setShowAllMinerals((v) => !v)}
                className={`${HOME_CTA_DARK_OUTLINE} px-6 py-3 text-sm`}
              >
                {showAllMinerals
                  ? "Show fewer"
                  : `Show all ${TRACE_MINERALS_COUNT}`}
              </button>
            </div>
          ) : null}

          {isFiltering && filteredMinerals.length === 0 ? (
            <p className="mt-6 text-sm text-sage m-0">No minerals match that filter.</p>
          ) : null}
        </section>

        {/* —— Amino acids —— */}
        <section id="amino-acids" className="scroll-mt-28 mb-16" aria-labelledby="amino-acids-heading">
          <header className="max-w-3xl mb-8">
            <p className="text-xs tracking-[0.25em] uppercase text-sage/80 m-0">Resource</p>
            <h2 id="amino-acids-heading" className="font-sans font-semibold text-3xl md:text-4xl text-ivory mt-3 m-0">
              Amino Acids
            </h2>
            <p className="mt-4 text-sm md:text-base text-sage leading-relaxed m-0">
              {SEA_MOSS_AMINO_ACIDS_INTRO}
            </p>
          </header>

          <ol className="grid md:grid-cols-2 gap-3 list-none m-0 p-0">
            {SEA_MOSS_AMINO_ACIDS.map((aa, i) => (
              <li key={aa.name}>
                <DarkCard className="h-full !p-5 md:!p-6">
                  <div className="flex items-baseline gap-3">
                    <span className="text-xs tabular-nums text-sage/60 shrink-0">{i + 1}.</span>
                    <div className="min-w-0">
                      <h3 className="font-sans font-semibold text-base text-ivory m-0">{aa.name}</h3>
                      <p className="mt-2 text-sm text-sage leading-relaxed m-0">{aa.blurb}</p>
                    </div>
                  </div>
                </DarkCard>
              </li>
            ))}
          </ol>

          <DarkCard className="mt-6 max-w-3xl border border-white/10">
            <p className="text-sm text-sage leading-relaxed m-0">{SEA_MOSS_AMINO_ACIDS_VARIABILITY_NOTE}</p>
          </DarkCard>
        </section>

        <WellnessDisclaimer className="max-w-3xl mb-12" />

        <div className="flex flex-wrap gap-3 mb-12">
          <Link to="/shop" className={`${HOME_CTA_DARK_PRIMARY} px-7 py-3.5 text-sm`}>
            Shop Moss Matrix
          </Link>
          <Link to="/learn/sea-moss-research" className={`${HOME_CTA_DARK_OUTLINE} px-7 py-3.5 text-sm`}>
            Research & citations
          </Link>
          <Link to="/learn/what-is-sea-moss" className={`${HOME_CTA_DARK_OUTLINE} px-7 py-3.5 text-sm`}>
            What is sea moss?
          </Link>
        </div>

        <ConversionCtas />

        <p className="text-sm text-sage text-center mt-10">
          <Link to="/learn" className="underline hover:text-goldhover">
            Learn hub
          </Link>
          {" · "}
          <Link to="/learn/sea-moss-benefits" className="underline hover:text-goldhover">
            Benefits
          </Link>
          {" · "}
          <Link to="/ingredients" className="underline hover:text-goldhover">
            Ingredients
          </Link>
          {" · "}
          <Link to="/faq" className="underline hover:text-goldhover">
            FAQ
          </Link>
        </p>
      </ContentPageLayout>
    </SiteShell>
  );
}
